import {
  Button,
  ButtonProps,
  Headline,
  Modal,
  GeneralModalProps as ModalProps,
} from '@zeiss/beyond-online-react';
import { useBreakpoint } from '@zeiss/lib-helpers';

import {
  ModalButtonContainer,
  ModalContainer,
  ModalContentContainer,
} from './ModalContainer';
import { Box } from 'src/snowflakes/Box';

interface GeneralModalProps extends Omit<ModalProps, 'declineButtonText'> {
  /**
   * The button label for the decline button.
   *
   * When this is not provided the decline button is not displayed
   */
  declineButtonText?: string;
  /**
   * Determine whether or not the primary action is disabled
   */
  disabled?: boolean;
  /**
   * Shows the loading state for the primary action button
   */
  loading?: boolean;
}

/**
 * The `GeneralModal` from beyond doesn't cover the ui/ux requirements from our designs.
 *
 *
 * This component implements the same behaviour and also adds functionality to:
 *
 * - show loading state for primary action button
 * - add layout fixes to match Identity Frontend designs
 */
export const GeneralModal = ({
  acceptButtonText,
  children,
  declineButtonText,
  disabled,
  headline,
  loading,
  onAccept,
  onDecline,
  ...modalProps
}: GeneralModalProps) => {
  const isMinTablet = useBreakpoint('tablet');

  const buttonSize: ButtonProps['size'] = isMinTablet ? 'l' : 'm';

  return (
    <Modal {...modalProps}>
      <ModalContainer>
        <ModalContentContainer>
          <Box spacing="s">
            <Headline headline={headline} />
          </Box>

          {children}
        </ModalContentContainer>

        <ModalButtonContainer>
          {declineButtonText ? (
            <Button
              disabled={loading}
              onClick={onDecline}
              size={buttonSize}
              variant="secondary"
            >
              {declineButtonText}
            </Button>
          ) : null}
          <Button
            disabled={disabled}
            loading={loading}
            onClick={onAccept}
            size={buttonSize}
          >
            {acceptButtonText}
          </Button>
        </ModalButtonContainer>
      </ModalContainer>
    </Modal>
  );
};
